"use client";

import { KeyRound } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { ErrorCode } from "@/generated/error-codes";
import { ApiError } from "@/shared/api-client";
import { loginWithPasskey } from "@/shared/auth/session-controller";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useLogin } from "../api/mutations";
import { postLoginTarget, redirectQuery } from "../post-login-target";

/** Passkey support depends on `window`/`PublicKeyCredential`, which only
 * exist client-side. Detecting it at module load (or first render) makes
 * the server prerender and the client hydration disagree → React #418
 * hydration mismatch that cascades into the error boundary. Gate on a
 * post-mount effect so BOTH renders agree (nothing), then reveal. */
function usePasskeySupport(): boolean {
  const [supported, setSupported] = useState(false);
  useEffect(() => {
    setSupported(
      typeof PublicKeyCredential !== "undefined" &&
        typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function",
    );
  }, []);
  return supported;
}

function PasskeySignInButton() {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const supported = usePasskeySupport();
  if (!supported) return null;
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await loginWithPasskey();
          router.replace(postLoginTarget(window.location.search));
        } catch (err) {
          if (!(err instanceof DOMException && err.name === "NotAllowedError")) {
            // A dismissed ceremony is not an error; anything else is quiet —
            // the password path is right there.
          }
        } finally {
          setBusy(false);
        }
      }}
    >
      <KeyRound className="size-4" />
      {busy ? "Waiting for your device…" : "Sign in with a passkey"}
    </Button>
  );
}

export function SignInForm() {
  const router = useRouter();
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          // Read ?redirect lazily at submit time (client-only) rather than via
          // useSearchParams at render — the latter would mark the whole form a
          // request-time dynamic hole under cacheComponents, keeping it out of
          // the static shell.
          router.replace(postLoginTarget(window.location.search));
        },
        onError: (err) => {
          // First factor passed, second owed — no tokens exist yet.
          // Stash the gate (sessionStorage, not URL: survives refresh,
          // never lands in history/referrer) and enter the MFA leg.
          if (err instanceof ApiError && err.is(ErrorCode.AUTH_MFA_REQUIRED)) {
            const details = err.details as { challenge_id?: string; methods?: string[] };
            try {
              window.sessionStorage.setItem(
                "tailstrike.auth.mfaGate",
                JSON.stringify({
                  challengeId: details.challenge_id,
                  methods: details.methods ?? ["totp"],
                }),
              );
            } catch {
              /* private mode — the page falls back to its empty state */
            }
            router.push(`/mfa-challenge${redirectQuery(window.location.search)}`);
          }
        },
      },
    );
  };

  const errorMessage = (() => {
    const err = login.error;
    if (!err) return null;
    if (err instanceof ApiError && err.is(ErrorCode.AUTH_MFA_REQUIRED)) {
      return null; // not an error — we're navigating to /mfa-challenge
    }
    if (err instanceof ApiError && err.is(ErrorCode.AUTH_INVALID_CREDENTIALS)) {
      return "Email or password is incorrect.";
    }
    if (err instanceof ApiError && err.is(ErrorCode.AUTH_TOO_MANY_ATTEMPTS)) {
      return "Too many attempts. Wait a minute and try again.";
    }
    if (err instanceof ApiError) return err.message;
    return "Could not reach the server. Check your connection and try again.";
  })();

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <a
            href="/forgot-password"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Forgot password?
          </a>
        </div>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {errorMessage ? (
        <p className="text-sm text-destructive" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? "Signing in…" : "Sign in"}
      </Button>
      <PasskeySignInButton />
      <div className="text-center">
        {/* Its own page, not a control under the password form: one field and
            one button, with nothing competing for the click. */}
        <Link
          href="/sign-in-link"
          className="text-xs font-medium text-foreground underline underline-offset-4 hover:text-primary"
        >
          Email me a sign-in link instead
        </Link>
      </div>
    </form>
  );
}
